import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { IUserWithMetadata } from '@/types/Auth/User.types';
import { LOCAL_DEV_USER, IScopeResp } from '@zero-dash/types';
import { Scope } from '@/types/Auth/Scope.types';
import { useZeroDashSdk } from './ZeroDashSdkContext';

interface IProps {
  children: ReactNode;
}

interface IUserContext {
  currentUser?: IUserWithMetadata | null;
  loading: boolean;
  error?: string;
  assignedCuratorScopes: IScopeResp<Scope>[];
  assignedClinicianScopes: IScopeResp<Scope>[];
  isAdmin: boolean;
}

export const UserContext = createContext<IUserContext>({
  loading: true,
  assignedCuratorScopes: [],
  assignedClinicianScopes: [],
  isAdmin: false,
});

UserContext.displayName = 'UserContext';

export const useUser = (): IUserContext => useContext(UserContext);

/**
 * Provides the signed-in user and the scope sets for the assigned-curator and
 * assigned-clinician pseudo-roles.
 */
export function UserProvider({ children }: IProps): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();

  const [currentUser, setCurrentUser] = useState<IUserWithMetadata | null>();
  const [assignedCuratorScopes, setAssignedCuratorScopes] = useState<IScopeResp<Scope>[]>([]);
  const [assignedClinicianScopes, setAssignedClinicianScopes] = useState<IScopeResp<Scope>[]>([]);

  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (import.meta.env.VITE_DISABLE_AUTH === 'true') {
      setCurrentUser(LOCAL_DEV_USER as unknown as IUserWithMetadata);
      setLoading(false);
      return;
    }

    async function getCurrentUser(): Promise<void> {
      const currUser = await zeroDashSdk.services.auth.getCurrentUser();
      setCurrentUser(currUser);
    }

    async function getAssignedScopes(): Promise<void> {
      const rolesResp = await zeroDashSdk.services.auth.getRoles();

      const assignedCuratorId = rolesResp.find((r) => r.name === 'ZeroDash Assigned Curator');
      const curatorScopes = await zeroDashSdk.services.auth.getScopesByRoleId(assignedCuratorId?.id || '');
      setAssignedCuratorScopes(curatorScopes);

      const assignedClinicianId = rolesResp.find((r) => r.name === 'ZeroDash Assigned Clinician');
      const clinicianScopes = await zeroDashSdk.services.auth.getScopesByRoleId(assignedClinicianId?.id || '');
      setAssignedClinicianScopes(clinicianScopes);
    }

    async function loadUser(): Promise<void> {
      setLoading(true);

      try {
        await getCurrentUser();
        await getAssignedScopes();
      } catch {
        setError('An error occurred while getting current user');
      } finally {
        setLoading(false);
      }
    }

    loadUser();
  }, [zeroDashSdk.patient, zeroDashSdk.services.auth]);

  const isAdmin = useMemo<boolean>(
    () => import.meta.env.VITE_DISABLE_AUTH === 'true'
      || Boolean(currentUser?.roles?.some((r) => r.name === 'ZeroDash Admin')),
    [currentUser],
  );

  const value = useMemo<IUserContext>(() => ({
    currentUser,
    loading,
    error,
    assignedCuratorScopes,
    assignedClinicianScopes,
    isAdmin,
  }), [
    currentUser,
    loading,
    error,
    assignedCuratorScopes,
    assignedClinicianScopes,
    isAdmin,
  ]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}
